import {
  CommonPageConfig,
  SupportedDevice,
  UiPageConfig,
  UiPagePageConfiguration,
} from '@finxone-platform/shared/sys-config-types';

export function getModalConfigStyle(pageConfig: UiPageConfig, activeDevice: SupportedDevice) {
  const pageConfiguration = onSetDefaultModalValue<UiPagePageConfiguration>(
    pageConfig?.pageConfiguration as UiPagePageConfiguration,
    activeDevice,
  );

  const modalConfiguration = pageConfiguration?.modalConfiguration;

  const modalHeightAndWidth = modalConfiguration?.cssEnabled
    ? {}
    : {
        height: `${modalConfiguration?.modalSizeHeight}${modalConfiguration?.modalSizeMarginUnit}`,
        width: `${modalConfiguration?.modalSizeWidth}${modalConfiguration?.modalSizeMarginUnit}`,
      };

  const style = {
    ...modalHeightAndWidth,
    backgroundColor: pageConfiguration?.backgroundColor ?? 'var(--color-background)',
    borderTopLeftRadius: `${pageConfiguration?.borderConfig?.borderRadius?.topLeft}${pageConfiguration?.borderConfig?.borderRadius?.unit}`,
    borderBottomLeftRadius: `${pageConfiguration?.borderConfig?.borderRadius?.bottomLeft}${pageConfiguration?.borderConfig?.borderRadius?.unit}`,
    borderTopRightRadius: `${pageConfiguration?.borderConfig?.borderRadius?.topRight}${pageConfiguration?.borderConfig?.borderRadius?.unit}`,
    borderBottomRightRadius: `${pageConfiguration?.borderConfig?.borderRadius?.bottomRight}${pageConfiguration?.borderConfig?.borderRadius?.unit}`,
    boxShadow: `${modalConfiguration?.shadowOffsetX}px ${modalConfiguration?.shadowOffsetY}px ${modalConfiguration?.shadowBlur}px ${modalConfiguration?.shadowSpread}px ${modalConfiguration?.shadowColour}`,
    border: pageConfiguration?.borderConfig?.width
      ? `${pageConfiguration?.borderConfig?.width}${pageConfiguration?.borderConfig?.unit} solid ${pageConfiguration?.borderConfig?.color}`
      : undefined,
  };
  const marginStyle = onGetModalConfigMargin(pageConfig);
  const paddingStyle = onGetModalConfigPadding(pageConfig);
  const closeIconContentStyle = onGetCloseIconContentStyle(pageConfig);

  return { contentStyle: style, marginStyle, paddingStyle, closeIconContentStyle };
}

const onGetModalConfigMargin = (pageConfig: UiPageConfig) => {
  const modalConfiguration = pageConfig.pageConfiguration?.modalConfiguration;
  const style = {
    marginTop: getSpacingValue(modalConfiguration?.marginTop, modalConfiguration?.marginUnit),
    marginBottom: getSpacingValue(modalConfiguration?.marginBottom, modalConfiguration?.marginUnit),
    marginLeft: getSpacingValue(modalConfiguration?.marginLeft, modalConfiguration?.marginUnit),
    marginRight: getSpacingValue(modalConfiguration?.marginRight, modalConfiguration?.marginUnit),
  };
  return style;
};

const onGetModalConfigPadding = (pageConfig: UiPageConfig) => {
  const modalConfiguration = pageConfig.pageConfiguration?.modalConfiguration;
  const style = {
    paddingTop: getSpacingValue(modalConfiguration?.paddingTop, modalConfiguration?.paddingUnit),
    paddingBottom: getSpacingValue(modalConfiguration?.paddingBottom, modalConfiguration?.paddingUnit),
    paddingLeft: getSpacingValue(modalConfiguration?.paddingLeft, modalConfiguration?.paddingUnit),
    paddingRight: getSpacingValue(modalConfiguration?.paddingRight, modalConfiguration?.paddingUnit),
  };
  return style;
};

/**
 * Unset spacing must be `undefined`, not "undefinedundefined": the browser ignores the invalid
 * string and keeps the previous modal's value when one modal navigates to another (same element).
 */
const getSpacingValue = (value: number | string | undefined, unit: string | undefined) => {
  return value === undefined || value === null || value === '' || !unit ? undefined : `${value}${unit}`;
};

const onGetCloseIconContentStyle = (pageConfig: UiPageConfig) => {
  const modalConfiguration = pageConfig.pageConfiguration?.modalConfiguration;
  const style = {
    top: getNumberValue(
      modalConfiguration?.closeIconTopPosition,
      modalConfiguration?.closeIconTopPositionUnit,
    ),
    bottom: getNumberValue(
      modalConfiguration?.closeIconBottomPosition,
      modalConfiguration?.closeIconBottomPositionUnit,
    ),
    left: getNumberValue(
      modalConfiguration?.closeIconLeftPosition,
      modalConfiguration?.closeIconLeftPositionUnit,
    ),
    right: getNumberValue(
      modalConfiguration?.closeIconRightPosition,
      modalConfiguration?.closeIconRightPositionUnit,
    ),
  };
  return style;
};

const getNumberValue = (closeIconProperty: number | undefined, unit: string | undefined) => {
  return Number(closeIconProperty) ? `${closeIconProperty}${unit}` : undefined;
};

export const onSetDefaultModalValue = <T extends UiPagePageConfiguration | CommonPageConfig>(
  pageConfiguration: T,
  activeDevice: SupportedDevice,
) => {
  if (!Number(pageConfiguration?.modalConfiguration?.modalSizeWidth)) {
    pageConfiguration = {
      ...pageConfiguration,
      modalConfiguration: {
        ...pageConfiguration?.modalConfiguration,
        modalSizeWidth: activeDevice === 'mobile' ? 400 : 600,
        modalSizeMarginUnit: 'px',
      },
    };
  }
  if (!Number(pageConfiguration?.modalConfiguration?.modalSizeHeight)) {
    pageConfiguration = {
      ...pageConfiguration,
      modalConfiguration: {
        ...pageConfiguration?.modalConfiguration,
        modalSizeHeight: activeDevice === 'mobile' ? 500 : 600,
        modalSizeMarginUnit: 'px',
      },
    };
  }

  if (!Number(pageConfiguration?.modalConfiguration?.closeIconSize)) {
    pageConfiguration = {
      ...pageConfiguration,
      modalConfiguration: {
        ...pageConfiguration?.modalConfiguration,
        closeIconSize: 32,
        closeIconUnit: 'px',
      },
    };
  }

  if (!pageConfiguration?.backgroundColor) {
    pageConfiguration = {
      ...pageConfiguration,
      backgroundColor: '#FFFFFF',
    };
  }
  if (!pageConfiguration?.borderConfig?.width) {
    pageConfiguration = {
      ...pageConfiguration,
      borderConfig: {
        ...pageConfiguration?.borderConfig,
        width: undefined,
        unit: undefined,
      },
    };
  }
  return pageConfiguration;
};

/**
 * Constructs a modal page URL for logged-in users.
 *
 * @param zoneUrl - The zone identifier to be included in the modal URL.
 * @param pageUrl - The page identifier to be included in the modal URL.
 * @param isPrevModal - (Optional) Override to indicate if the previous navigation was a modal.
 *                      If true or undefined, keeps modal state; if false, disables modal state.
 * @returns The constructed modal page URL with modal state query parameter.
 */
export const createLoggedInModalPageUrl = ({
  zoneUrl,
  pageUrl,
  isPrevModal,
}: {
  pageUrl: string;
  zoneUrl: string;
  isPrevModal?: boolean;
}) => {
  const isCurrentNavigationModalPage = location.pathname.includes('modal:');
  const shouldStayModal = isCurrentNavigationModalPage && (isPrevModal ?? true);
  // Crete the base URL without any existing modals
  const parentZonePath = location.pathname.replace(/\(.*?\)/g, '')?.replace(/\/+$/, '');

  return `${parentZonePath}/(modal:modal/${zoneUrl}/${pageUrl})${
    shouldStayModal ? '?isModal=true' : '?isModal=false'
  }`;
};

export const createLoggedOutModalPageUrl = ({
  zoneUrl,
  pageUrl,
  url,
  role,
}: {
  pageUrl: string;
  zoneUrl: string;
  url: string;
  role: string;
}) => {
  const isCurrentNavigationModalPage = location.pathname.includes('modal:');
  // Crete the base URL without any existing modals
  const parentZonePath = location.pathname.replace(/\(.*?\)/g, '')?.replace(/\/+$/, '');

  return `${parentZonePath}/(modal:modal/${zoneUrl}/${pageUrl}/${role}/default)${
    isCurrentNavigationModalPage ? '?isModal=true' : '?isModal=false'
  }`;
};

/**
 * Retrieves the current browser path and strips out any Angular secondary route
 * segment used for modals.
 * * This effectively returns the URL of the primary page that is currently visible
 * behind an open modal.
 * * Example:
 * Input: '/zones/onboarding/review-information/thisbank/default/(modal:modal/onboard-failed/thisbank/default)'
 * Output: '/zones/onboarding/review-information/thisbank/default'
 * * @returns The primary route path with all auxiliary route segments removed.
 */
export const stripModalFromLocation = () => {
  // to remove the modal page url and get the previous page's url the screen.
  return location.pathname?.replace(/\(.*?\)/g, '');
};
